// src/components/SchedulePage.js
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { firestore } from '../firebase';
import { useSearchParams, useParams, useLocation } from 'react-router-dom';
import * as PublicEvent from '../utils/activeEvent';
import { fetchSponsorsMergedForEvent } from '../utils/sponsorQueries';
import { fetchSiteRegistrationSettings } from '../utils/siteSettings';
import { getPublicSiteMarketingConfig } from '../utils/publicSiteConfig';
import { migrateScheduleFieldsForEditor } from '../constants/defaultSchedule';
import { getEventEditionYear } from '../utils/eventRouting';
import { anchorIdForSlug } from '../utils/scheduleAnchors';
import PublicEventChrome from './PublicEventChrome';
import ScheduleStructuredLayout from './schedule/ScheduleStructuredLayout';
import './SchedulePage.css';

function SchedulePage() {
  const [searchParams] = useSearchParams();
  const { year: yearParam, scheduleSlug: scheduleSlugParam } = useParams();
  const location = useLocation();
  const eventIdParam = searchParams.get('event');
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedPastDays, setExpandedPastDays] = useState(() => new Set());
  const [sponsors, setSponsors] = useState([]);
  const [registrationOpen, setRegistrationOpen] = useState(false);
  const [registrationUrl, setRegistrationUrl] = useState('');
  const [siteInterestUrl, setSiteInterestUrl] = useState('');
  const [marketingHomeUrl, setMarketingHomeUrl] = useState('');

  const togglePastDay = useCallback((dayId) => {
    setExpandedPastDays((prev) => {
      const next = new Set(prev);
      if (next.has(dayId)) next.delete(dayId);
      else next.add(dayId);
      return next;
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [reg, mkt] = await Promise.all([
          fetchSiteRegistrationSettings(),
          getPublicSiteMarketingConfig(),
        ]);
        if (!cancelled) {
          setRegistrationOpen(reg.registrationOpen);
          setRegistrationUrl(reg.registrationUrl);
          setSiteInterestUrl(reg.interestListUrl);
          setMarketingHomeUrl(mkt.marketingSiteBaseUrl || '');
        }
      } catch {
        if (!cancelled) {
          setRegistrationOpen(false);
          setRegistrationUrl('');
          setSiteInterestUrl('');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    async function loadSchedule() {
      try {
        const id = await PublicEvent.resolvePublicPageEventId({
          eventIdParam,
          editionYearParam: yearParam,
        });
        let eventDoc = null;
        if (id) {
          const snap = await getDoc(doc(firestore, 'events', id));
          if (snap.exists()) eventDoc = { id: snap.id, ...snap.data() };
        }
        setEvent(eventDoc);
        if (eventDoc) {
          const sponsorListRaw = await fetchSponsorsMergedForEvent(eventDoc.id);
          const sponsorList = sponsorListRaw
            .filter(
              (s) =>
                !s.hidden &&
                (s.type || 'none') !== 'none' &&
                (s.showOnSchedule !== false)
            );
          sponsorList.sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
          setSponsors(sponsorList);
        } else {
          setSponsors([]);
        }
      } catch (err) {
        setError(err.message || 'Failed to load schedule');
      } finally {
        setLoading(false);
      }
    }
    loadSchedule();
  }, [eventIdParam, yearParam]);

  const scheduleDataRaw = event?.scheduleData || null;
  const scheduleData = useMemo(
    () => (scheduleDataRaw ? migrateScheduleFieldsForEditor(scheduleDataRaw) : null),
    [scheduleDataRaw]
  );
  const rawHtml = (event?.scheduleHtml || '').trim();
  const hasStructured = !!scheduleData;
  const hasLegacyHtml = !hasStructured && rawHtml.length > 0;

  const scrollTarget = useMemo(() => {
    const fromPath = (scheduleSlugParam || '').trim().toLowerCase();
    const fromHash = (location.hash || '').replace(/^#/, '').trim().toLowerCase();
    return fromPath || fromHash || '';
  }, [scheduleSlugParam, location.hash]);

  useEffect(() => {
    if (loading || !event || !hasStructured || !scrollTarget) return undefined;
    const id = anchorIdForSlug(scrollTarget);
    if (!id) return undefined;
    const t = window.setTimeout(() => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        el.classList.add('schedule-anchor-highlight');
        window.setTimeout(() => el.classList.remove('schedule-anchor-highlight'), 2200);
      }
    }, 100);
    return () => window.clearTimeout(t);
  }, [loading, event, hasStructured, scrollTarget]);

  /** Past days expanded by default; reset when switching event or schedule payload. */
  useEffect(() => {
    if (!scheduleData?.days?.length) return;
    setExpandedPastDays(new Set(scheduleData.days.map((d) => d.id)));
  }, [event?.id, scheduleData]);

  const eventQueryId = (eventIdParam || event?.id || '').toString().trim();
  const editionYearFallback = event ? getEventEditionYear(event) : null;
  const fallbackInterestUrl = useMemo(() => {
    const fromCta = (scheduleData?.cta?.interestListUrl || '').trim();
    return (fromCta || siteInterestUrl || marketingHomeUrl || '').trim();
  }, [scheduleData, siteInterestUrl, marketingHomeUrl]);

  if (loading) {
    return (
      <div className="schedule-page schedule-page--marketing">
        <PublicEventChrome
          activeNav="schedule"
          eventQueryId={eventIdParam || ''}
          editionYearParam={yearParam}
          editionYearFallback={editionYearFallback}
        />
        <div className="schedule-mkt-container">
          <p className="schedule-loading">Loading schedule…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="schedule-page schedule-page--marketing">
        <PublicEventChrome
          activeNav="schedule"
          eventQueryId={eventIdParam || ''}
          editionYearParam={yearParam}
          editionYearFallback={editionYearFallback}
        />
        <div className="schedule-mkt-container">
          <p className="schedule-error">{error}</p>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="schedule-page schedule-page--marketing">
        <PublicEventChrome
          activeNav="schedule"
          eventQueryId={eventIdParam || ''}
          editionYearParam={yearParam}
          editionYearFallback={editionYearFallback}
        />
        <div className="schedule-mkt-container">
          <p className="schedule-error">No event found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="schedule-page schedule-page--marketing">
      <PublicEventChrome
        activeNav="schedule"
        eventQueryId={eventQueryId}
        editionYearParam={yearParam}
        editionYearFallback={editionYearFallback}
      />
      <div className="schedule-mkt-container">
        {hasStructured ? (
          <ScheduleStructuredLayout
            event={event}
            scheduleData={scheduleData}
            sponsors={sponsors}
            registrationOpen={registrationOpen}
            registrationUrl={registrationUrl}
            fallbackInterestUrl={fallbackInterestUrl}
            expandedPastDays={expandedPastDays}
            togglePastDay={togglePastDay}
          />
        ) : hasLegacyHtml ? (
          <div
            className="schedule-html-wrapper"
            // Admin-authored content only (legacy HTML)
            dangerouslySetInnerHTML={{ __html: rawHtml }}
          />
        ) : (
          <div className="schedule-empty-card">
            <h2>Schedule not published yet</h2>
            <p>
              The detailed event schedule hasn&apos;t been posted. Check back closer to the event date for the
              latest times and activities.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default SchedulePage;
